import React, { useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { XMarkIcon as XMarkOutline } from '@heroicons/react/24/outline';
import ChatAvatar from './ChatAvatar';
import { useTenantMembers } from '../../hooks/chat/useTenantMembers';
import type { TenantMember } from '../../types/chat';

interface RecipientPickerProps {
  selected: TenantMember[];
  onChange: (next: TenantMember[]) => void;
  multi?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  onCancel?: () => void;
}

/**
 * Inline chip-input recipient picker — matches Figma `6138-71319`.
 *
 * - "To:" label sits OUTSIDE the input on the left.
 * - The bordered field (h-12, rounded 8 px, max-w-[320px]) hosts soft-gray
 *   chips (32 px tall, pill-shaped) and a single growing text input.
 * - The dropdown sits directly below the field with a backdrop-blurred
 *   glass surface and a purple checkmark on already-selected rows so the
 *   picker doubles as a multi-select toggle.
 * - The trailing X button calls `onCancel` to abandon the whole compose
 *   flow (the chips have their own per-item X).
 */
const RecipientPicker: React.FC<RecipientPickerProps> = ({
  selected,
  onChange,
  multi = false,
  placeholder = '+Recipients',
  autoFocus,
  onCancel,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const { data } = useTenantMembers({ search, include_self: false, per_page: 20 });

  // Show *every* search result (selected and not), so the dropdown acts
  // as a multi-select toggle. Selected rows render with a checkmark.
  const candidates = data?.members ?? [];
  const isSelected = (m: TenantMember) =>
    selected.some((s) => s.user_id === m.user_id);

  const togglePick = (member: TenantMember) => {
    if (isSelected(member)) {
      onChange(selected.filter((s) => s.user_id !== member.user_id));
      return;
    }
    if (multi) {
      onChange([...selected, member]);
      setSearch('');
    } else {
      onChange([member]);
      setSearch('');
      setOpen(false);
    }
  };

  const removeChip = (member: TenantMember) => {
    onChange(selected.filter((s) => s.user_id !== member.user_id));
  };

  return (
    <div className="flex items-center gap-4 w-full">
      <span className="text-sm font-semibold text-text-secondary shrink-0">To:</span>

      <div className="relative flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-h-[48px] px-2 py-1.5 border border-border-secondary rounded-lg bg-surface-primary focus-within:border-primary-400 transition-colors">
          {selected.map((m) => (
            <span
              key={m.user_id}
              className="inline-flex items-center h-8 pl-1 pr-1.5 rounded-[10px] bg-neutral-100/80 dark:bg-neutral-800/60 text-text-primary text-[13px] font-medium gap-1.5 shrink-0"
            >
              <ChatAvatar src={m.avatar_url} name={m.full_name} size={24} />
              <span className="truncate max-w-[120px]">{m.full_name}</span>
              <button
                type="button"
                aria-label={`Remove ${m.full_name}`}
                onClick={() => removeChip(m)}
                className="text-text-tertiary hover:text-text-primary opacity-50 hover:opacity-100"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {(multi || selected.length === 0) && (
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={selected.length ? '' : placeholder}
              autoFocus={autoFocus}
              className="flex-1 min-w-[80px] bg-transparent text-sm text-text-primary placeholder:text-text-disabled focus:outline-none"
            />
          )}
          {onCancel && (
            <button
              type="button"
              aria-label="Cancel new chat"
              onClick={onCancel}
              className="shrink-0 p-1 rounded-full text-text-tertiary opacity-50 hover:opacity-100 hover:text-text-primary"
            >
              <XMarkOutline className="w-4 h-4" />
            </button>
          )}
        </div>

        {open && candidates.length > 0 && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <ul
              className="absolute z-40 left-0 top-[calc(100%+4px)] w-[280px] max-h-[280px] overflow-y-auto p-1 rounded-[10px] bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl ring-1 ring-black/5 dark:ring-white/10 shadow-[0_12px_28px_-8px_rgba(15,15,28,0.14),0_4px_10px_-4px_rgba(15,15,28,0.08)] scrollbar-thin"
              role="listbox"
            >
              {candidates.map((m) => {
                const picked = isSelected(m);
                return (
                  <li key={m.user_id}>
                    <button
                      type="button"
                      onClick={() => togglePick(m)}
                      className="relative w-full flex items-center gap-2 px-2 py-1.5 rounded-full text-left hover:bg-neutral-100/60 dark:hover:bg-neutral-800/40 transition-colors"
                    >
                      <div className="relative shrink-0">
                        <ChatAvatar src={m.avatar_url} name={m.full_name} size={32} />
                        {picked && (
                          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-primary-500/20 backdrop-blur-sm">
                            <CheckIcon className="w-4 h-4 text-primary-600 dark:text-primary-350" />
                          </span>
                        )}
                      </div>
                      <span className="flex-1 min-w-0 text-sm text-text-primary truncate">
                        {m.full_name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default RecipientPicker;
